import React from 'react';

/**
 * Base Button component with variants and sizes
 * @param {Object} props
 * @param {string} props.variant - Button variant (primary, secondary, danger, hero-primary, hero-secondary)
 * @param {string} props.size - Button size (sm, md, lg)
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Button content
 * @param {Object} props.rest - Additional props
 * @returns {JSX.Element}
 */
export const Button = ({ 
  variant = 'primary', 
  size = 'md', 
  disabled = false, 
  className = '', 
  children, 
  ...rest 
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantClasses = {
    primary: 'border-2 bg-primary-600 text-white border-primary-600 hover:bg-white hover:text-primary-600 focus:ring-primary-500',
    secondary: 'border-2 bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 hover:border-gray-300',
    danger: 'bg-error-600 text-white hover:bg-error-700 focus:ring-error-500',
    'hero-primary': 'border-2 bg-white text-primary-600 border-white hover:border-gray-200 hover:bg-gray-200 hover:text-primary-600',
    'hero-secondary': 'border-2 bg-transparent text-white border-white hover:border-white hover:bg-white hover:text-primary-600'
  };
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-md',
    lg: 'px-6 py-3 text-base'
  };
  
  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;
  
  return (
    <button 
      className={classes} 
      disabled={disabled}
      {...rest}
    >
      {children}
    </button>
  );
};

/**
 * Primary button wrapper
 */
export const PrimaryButton = ({ className = '', ...props }) => (
  <Button variant="primary" className={className} {...props} />
);

/**
 * Secondary button wrapper
 */
export const SecondaryButton = ({ className = '', ...props }) => (
  <Button variant="secondary" className={className} {...props} />
);

/**
 * Danger button wrapper
 */
export const DangerButton = ({ className = '', ...props }) => (
  <Button variant="danger" className={className} {...props} />
);

/**
 * Hero primary button wrapper (for public page)
 */
export const HeroPrimaryButton = ({ className = '', ...props }) => (
  <Button variant="hero-primary" className={className} {...props} />
);

/**
 * Hero secondary button wrapper (for public page)
 */
export const HeroSecondaryButton = ({ className = '', ...props }) => (
  <Button variant="hero-secondary" className={className} {...props} />
); 